/**
 * ButtonWidget - Interactive button widget with full inline editors
 *
 * The most important widget for voice apps! Buttons can be activated
 * by clicking OR by voice commands. Double-click to edit the label.
 *
 * When selected, shows:
 * - Variant selector (primary/secondary/ghost/danger)
 * - Icon picker (emoji)
 * - Voice settings panel (trigger phrases, speakable, description)
 */

import { memo, useState, useRef, useEffect } from 'react';
import type { ButtonWidget as ButtonWidgetType } from '@deep-agents/shared-types';
import type { WidgetComponentProps } from '../types';
import { VoiceConfigEditor } from './VoiceConfigEditor';
import { IconPicker } from './IconPicker';

interface ButtonWidgetProps extends Omit<WidgetComponentProps, 'widget'> {
  widget: ButtonWidgetType;
}

// Button variant styles
const variantClasses: Record<string, string> = {
  primary: 'bg-violet-600 hover:bg-violet-700 text-white',
  secondary: 'bg-gray-100 hover:bg-gray-200 text-gray-800',
  ghost: 'bg-transparent hover:bg-gray-100 text-gray-700',
  danger: 'bg-red-600 hover:bg-red-700 text-white',
};

export const ButtonWidgetComponent = memo(function ButtonWidgetComponent({
  widget,
  isSelected,
  onSelect,
  onUpdate,
}: ButtonWidgetProps) {
  const { props, voice } = widget;
  const variant = props.variant || 'primary';

  // Local editing state
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(props.label || 'Button');
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Sync with props
  useEffect(() => {
    setEditValue(props.label || 'Button');
  }, [props.label]);

  // Handle double-click to edit
  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsEditing(true);
  };

  // Save changes
  const handleSave = () => {
    setIsEditing(false);
    if (onUpdate && editValue !== props.label) {
      onUpdate({
        ...widget,
        props: {
          ...widget.props,
          label: editValue || 'Button',
        },
      });
    }
  };

  // Handle keyboard
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    }
    if (e.key === 'Escape') {
      setEditValue(props.label || 'Button');
      setIsEditing(false);
    }
  };

  // Handle variant change
  const handleVariantChange = (newVariant: string) => {
    if (!onUpdate) return;
    onUpdate({
      ...widget,
      props: { ...widget.props, variant: newVariant as ButtonWidgetType['props']['variant'] },
    });
  };

  // Handle icon change
  const handleIconChange = (icon: string | undefined) => {
    if (!onUpdate) return;
    onUpdate({
      ...widget,
      props: { ...widget.props, icon },
    });
  };

  // Handle voice config change
  const handleVoiceChange = (voiceConfig: typeof voice) => {
    if (!onUpdate) return;
    onUpdate({
      ...widget,
      voice: voiceConfig,
    });
  };

  // Build voice trigger tooltip
  const voiceTriggers = voice?.triggerPhrases?.join(', ') || '';

  return (
    <div
      className={`
        relative rounded-lg transition-all duration-200 cursor-pointer p-1
        ${isSelected ? 'ring-2 ring-violet-500 ring-offset-2' : ''}
      `}
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
      onDoubleClick={handleDoubleClick}
    >
      {/* The actual button (non-functional in builder, just preview) */}
      <button
        type="button"
        className={`
          w-full px-4 py-2.5 rounded-lg font-medium transition-colors
          flex items-center justify-center gap-2
          ${variantClasses[variant]}
        `}
        onClick={(e) => e.preventDefault()} // Prevent any action in builder
      >
        {/* Icon placeholder */}
        {props.icon && (
          <span className="text-lg">{props.icon}</span>
        )}

        {isEditing ? (
          <input
            ref={inputRef}
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            onClick={(e) => e.stopPropagation()}
            className="nodrag nopan bg-transparent border-b-2 border-white/50 text-center font-medium outline-none w-full"
            placeholder="Button label"
          />
        ) : (
          <span title="Double-click to edit">{props.label || 'Button'}</span>
        )}
      </button>

      {/* Voice trigger badge */}
      {voice?.triggerPhrases && voice.triggerPhrases.length > 0 && (
        <div
          className="absolute -bottom-6 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-violet-100 rounded-full text-[10px] text-violet-700 font-medium whitespace-nowrap"
          title={`Say: ${voiceTriggers}`}
        >
          🗣️ "{voice.triggerPhrases[0]}"
        </div>
      )}

      {/* Action indicator */}
      {widget.action && (
        <div
          className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center"
          title={`Action: ${widget.action.type}`}
        >
          <svg
            className="w-3 h-3 text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
            />
          </svg>
        </div>
      )}

      {/* ─── INLINE EDITOR (shown when selected and NOT label-editing) ─── */}
      {isSelected && !isEditing && (
        <div
          className="mt-2 space-y-2"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Variant selector */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-medium text-gray-500">Style:</span>
            {(['primary', 'secondary', 'ghost', 'danger'] as const).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => handleVariantChange(v)}
                className={`
                  nodrag nopan px-2 py-0.5 text-[10px] rounded border transition-colors
                  ${variant === v
                    ? 'bg-violet-100 text-violet-700 border-violet-300'
                    : 'bg-white text-gray-500 border-gray-200 hover:border-violet-200'
                  }
                `}
              >
                {v.charAt(0).toUpperCase() + v.slice(1)}
              </button>
            ))}
          </div>

          {/* Icon picker */}
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-medium text-gray-500">Icon:</span>
            <IconPicker value={props.icon} onChange={handleIconChange} />
          </div>

          {/* Voice settings (expandable - replaces old trigger phrase input) */}
          <VoiceConfigEditor config={voice} onChange={handleVoiceChange} />

          <p className="text-[9px] text-gray-400">
            Double-click button to edit label
          </p>
        </div>
      )}
    </div>
  );
});
